<template>
  <validation-observer v-if="dialogComputed" ref="observer" v-slot="{ invalid }">
    <v-dialog v-model="dialogComputed" max-width="1000px" scrollable persistent>
      <v-card>
        <v-card-title class="primary white--text py-2">
          {{ setting.title }}
          <v-spacer />
          <Btn icon="mdi-close" @click="close" depressed iconProp color="white" />
        </v-card-title>
        <v-card-text>
          <v-container fluid class="px-0">
            <v-row>
              <v-col cols="8">
                <PartCodeInput v-if="isMultipleEdit" :value="partCd" disabled />
                <PartCodeInput v-else :value="partCd" disabled />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <TextFieldRef v-if="isMultipleEdit" :label="$t('ELE0091')" value="" />
                <TextFieldInput
                  v-else
                  :label="$t('ELE0091')"
                  v-model="itemComputed.inspCharacCd"
                  rules="alphaNum"
                  :maxLength="2"
                  :debounce="500"
                  required
                />
              </v-col>
              <v-col cols="4">
                <TextFieldRef v-if="isMultipleEdit" :label="$t('ELE0092')" value="" />
                <TextFieldInput
                  v-else
                  :label="$t('ELE0092')"
                  v-model="itemComputed.inspCharacPoint"
                  :maxLength="charPointLimit"
                />
              </v-col>
              <v-col cols="4"></v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <SelectSingleInput
                  :label="$t('ELE0093')"
                  v-model="itemComputed.measFmtCd"
                  :items="measFmtCdItems"
                  item-value="value"
                  item-text="name"
                  @change="onChangeMeasFmtCd"
                  required
                />
              </v-col>
              <v-col cols="4">
                <SelectSingleInput
                  :label="$t('ELE0094')"
                  v-model="itemComputed.inspGradeCd"
                  :items="inspGradeCdItems"
                  item-value="value"
                  item-text="name"
                  required
                />
              </v-col>
              <v-col cols="4">
                <MeasuringInstrumentInput v-model="itemComputed.measInstCd" :required="measInstReq" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <TextFieldInput
                  v-if="itemComputed.measFmtCd == enumMeasFmtCd.NUMBER"
                  :label="$t('ELE0095')"
                  v-model="itemComputed.standard"
                  rules="inspCharacVal"
                />
                <TextFieldRef v-else :label="$t('ELE0095')" />
              </v-col>
              <v-col cols="4">
                <TextFieldInput
                  v-if="itemComputed.measFmtCd == enumMeasFmtCd.NUMBER"
                  :label="$t('ELE0096')"
                  v-model="itemComputed.upper"
                  rules="inspCharacVal"
                />
                <TextFieldRef v-else :label="$t('ELE0096')" />
              </v-col>
              <v-col cols="4">
                <TextFieldInput
                  v-if="itemComputed.measFmtCd == enumMeasFmtCd.NUMBER"
                  :label="$t('ELE0097')"
                  v-model="itemComputed.lower"
                  rules="inspCharacVal"
                />
                <TextFieldRef v-else :label="$t('ELE0097')" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4"></v-col>
              <v-col cols="4">
                <TextFieldInput
                  v-if="itemComputed.measFmtCd == enumMeasFmtCd.NUMBER"
                  :label="$t('ELE0538')"
                  v-model="itemComputed.clUpper"
                  rules="inspCharacVal"
                />
                <TextFieldRef v-else :label="$t('ELE0538')" />
              </v-col>
              <v-col cols="4">
                <TextFieldInput
                  v-if="itemComputed.measFmtCd == enumMeasFmtCd.NUMBER"
                  :label="$t('ELE0539')"
                  v-model="itemComputed.clLower"
                  rules="inspCharacVal"
                />
                <TextFieldRef v-else :label="$t('ELE0539')" />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <InspStandardInput v-model="itemComputed.inspStdCd" validOnly />
              </v-col>
              <v-col>
                <QltyStandardInput :label="$t('ELE0389')" v-model="itemComputed.qltyStdCd1" validOnly />
              </v-col>
              <v-col>
                <QltyStandardInput :label="$t('ELE0390')" v-model="itemComputed.qltyStdCd2" validOnly />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <TextFieldInput :label="$t('ELE0391')" v-model="itemComputed.qltyStd3" :maxLength="40" />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <TextAreaInput :label="$t('ELE0099')" :maxLength="1000" v-model="itemComputed.remarks" />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <span class="d-flex flex-row space-between">
            <Btn
              :label="setting.labelName"
              @click="onClickBtn(setting.eventName)"
              :disabled="isDisabled ? true : invalid"
            />
          </span>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </validation-observer>
</template>

<script>
import { API } from '@/plugins/api'
import { ValidationObserver } from 'vee-validate'
import { ModalMode, MeasFmtCd, SystemParameterKey, InspGradeCd } from '@/enum'
import Btn from '@/components/atoms/base/button/Btn'
import BtnAdd from '@/components/atoms/button/BtnAdd'
import PartCodeInput from '@/components/atoms/select/PartCodeInput'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import TextAreaInput from '@/components/atoms/base/text-field/TextAreaInput'
import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'
import MeasuringInstrumentInput from '@/components/atoms/select/MeasuringInstrumentInput'
import InspStandardInput from '@/components/atoms/select/InspStandardInput'
import QltyStandardInput from '@/components/atoms/select/QltyStandardInput'

export default {
  name: 'InspCharacDialog',
  components: {
    ValidationObserver,
    Btn,
    BtnAdd,
    PartCodeInput,
    TextFieldInput,
    TextFieldRef,
    TextAreaInput,
    SelectSingleInput,
    MeasuringInstrumentInput,
    InspStandardInput,
    QltyStandardInput,
  },

  props: {
    dialog: {
      type: Boolean,
      required: true,
      default: false,
    },
    mode: {
      type: String,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    partCd: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      charPointLimit: '',
      measInstReq: false,
      settings: [
        {
          // 追加モード時のテキストと処理
          mode: ModalMode.ADD,
          title: this.$t('ELE0016'),
          labelName: this.$t('ELE0016'),
          eventName: 'addItem',
        },
        {
          // 編集モード時のテキストと処理
          mode: ModalMode.EDIT,
          title: this.$t('ELE0017'),
          labelName: this.$t('ELE0105'),
          eventName: 'setItem',
        },
        {
          // 複数編集モード時のテキストと処理
          mode: ModalMode.MULTIPLE,
          title: this.$t('ELE0101'),
          labelName: this.$t('ELE0105'),
          eventName: 'editMultipleItems',
        },
      ],
    }
  },

  computed: {
    enumMeasFmtCd: () => MeasFmtCd,
    measFmtCdItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(MeasFmtCd).map((item) => {
          return { name: item + ' : ' + this.$t(`measFmtCd.${item}`), value: item }
        })
      },
    },
    inspGradeCdItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(InspGradeCd).map((item) => {
          return { name: item, value: item }
        })
      },
    },
    dialogComputed: {
      get() {
        return this.dialog
      },
      set(value) {
        this.$emit('update:dialog', value)
      },
    },
    itemComputed: {
      get() {
        return this.item
      },
      set(value) {
        this.$emit('update:item', value)
      },
    },
    setting: {
      get() {
        return this.settings.find((el) => el.mode === this.mode)
      },
    },
    isEdit: {
      get() {
        return this.setting.mode === ModalMode.EDIT
      },
    },
    isMultipleEdit: {
      get() {
        return this.setting.mode === ModalMode.MULTIPLE
      },
    },
    isDisabled: {
      get() {
        let result = false
        if (this.itemComputed.measFmtCd === MeasFmtCd.NUMBER) {
          if (this.itemComputed.standard == '' && this.itemComputed.upper == '' && this.itemComputed.lower == '') {
            result = true
          }
        }
        return result
      },
    },
  },

  methods: {
    close() {
      this.dialogComputed = false
    },
    onClickBtn(event) {
      this.$emit(event)
      this.dialogComputed = false
    },
    async onChangeMeasFmtCd() {
      await setImmediate(() => {})
      if (this.itemComputed.measFmtCd == MeasFmtCd.OK_NG) {
        this.itemComputed.standard = ''
        this.itemComputed.upper = ''
        this.itemComputed.lower = ''
      }
    },
    async setInitialParameters() {
      try {
        const param = await API.get(`/master/systemParameters?key=CHAR`)
        param?.forEach((element) => {
          if (element.key == SystemParameterKey.CHAR_POINT_LIMIT) {
            this.charPointLimit = Number(element.value) > 0 ? Number(element.value) : 10
          }
          if (element.key == SystemParameterKey.CHAR_MEAS_INST_REQ) {
            this.measInstReq = element.value == 'TRUE'
          }
        })
      } catch (error) {
        this.charPointLimit = 10
        this.measInstReq = false
        if (error.response.status != 404) {
          this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0348') })))
        }
      }
    },
  },
  async mounted() {
    await this.setInitialParameters()
  },
}
</script>
